import {
  addDoc,
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "./firebase";

function toMillis(v) {
  if (!v) return NaN;
  try {
    if (typeof v?.toDate === "function") return v.toDate().getTime();
    if (typeof v === "number") return v;
    return new Date(v).getTime();
  } catch {
    return NaN;
  }
}

function roundMoney(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function roundHours(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function hoursBetween(startV, endV) {
  const s = toMillis(startV);
  const e = toMillis(endV);
  if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) return 0;
  return roundHours((e - s) / 3600000);
}

function getAssignedWorkerUid(shift) {
  if (!shift) return "";
  if (shift.assignedWorkerUid) return shift.assignedWorkerUid;
  if (Array.isArray(shift.assignedWorkerUids) && shift.assignedWorkerUids.length > 0) {
    return shift.assignedWorkerUids[0];
  }
  return "";
}

function buildWorkerLabel(shift, workersByUid = new Map()) {
  const uid = getAssignedWorkerUid(shift);
  if (!uid) return "";
  const worker = workersByUid.get(uid);
  return (
    worker?.displayName ||
    worker?.fullName ||
    worker?.name ||
    worker?.email ||
    uid
  );
}

function resolveBillRate(shift, facilityRateMap = {}) {
  const explicit = Number(shift?.billRate);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;

  const role = String(shift?.role || "").toUpperCase().trim();
  const fallback = Number(facilityRateMap?.[role]);
  if (Number.isFinite(fallback) && fallback > 0) return fallback;

  return 0;
}

function makeDueDateFrom(base) {
  const ms = toMillis(base);
  const d = new Date(Number.isFinite(ms) ? ms : Date.now());
  d.setDate(d.getDate() + 14);
  return d;
}

export async function getEligibleShiftsForInvoice({
  tenantId,
  facilityId,
  billingStart,
  billingEnd,
}) {
  if (!facilityId) throw new Error("facilityId is required.");
  if (!billingStart || !billingEnd) {
    throw new Error("billingStart and billingEnd are required.");
  }

  if (!tenantId || tenantId === "all") throw new Error("tenantId is required.");
  const shiftsRef = collection(db, "shifts");
  const q = query(
    shiftsRef,
    where("tenantId", "==", tenantId),
    where("facilityId", "==", String(facilityId).toLowerCase()),
    where("status", "==", "completed"),
    orderBy("endTime", "asc")
  );

  const snap = await getDocs(q);

  const startMs = toMillis(billingStart);
  const endMs = toMillis(billingEnd);

  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
    throw new Error("Invalid billing range.");
  }

  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((shift) => {
      const shiftEnd = toMillis(shift.endTime);
      if (!Number.isFinite(shiftEnd)) return false;
      if (shiftEnd < startMs || shiftEnd > endMs) return false;
      if (shift.invoiceId) return false;
      if (String(shift.invoiceStatus || "").toLowerCase() === "billed") return false;
      return true;
    });
}

export function buildInvoiceDraft({
  tenantId,
  facilityId,
  facilityName = "",
  shifts = [],
  facilityRateMap = {},
  workersByUid = new Map(),
  adjustments = 0,
}) {
  if (!facilityId) throw new Error("facilityId is required.");
  if (!Array.isArray(shifts) || shifts.length === 0) {
    throw new Error("No eligible shifts to invoice.");
  }

  const lines = shifts.map((shift) => {
    const hours = hoursBetween(shift.startTime, shift.endTime);
    const billRate = resolveBillRate(shift, facilityRateMap);

    if (!billRate || billRate <= 0) {
      throw new Error(
        `Missing billRate for shift ${shift.id} (${shift.role || "unknown role"}).`
      );
    }

    const workerUid = getAssignedWorkerUid(shift);
    const workerName = buildWorkerLabel(shift, workersByUid);
    const lineTotal = roundMoney(hours * billRate);

    return {
      shiftId: shift.id,
      role: String(shift.role || "").toUpperCase().trim(),
      workerUid,
      workerName,
      workerEmail: workerName.includes("@") ? workerName : "",
      date: shift.startTime || null,
      startTime: shift.startTime || null,
      endTime: shift.endTime || null,
      hours,
      billRate,
      lineTotal,

      // backward-safe aliases
      rate: billRate,
      amount: lineTotal,
      workerLabel: workerName,
    };
  });

  const totalHours = roundHours(lines.reduce((sum, item) => sum + item.hours, 0));
  const subtotal = roundMoney(lines.reduce((sum, item) => sum + item.lineTotal, 0));
  const totalAmount = roundMoney(subtotal + Number(adjustments || 0));

  const sortedByEnd = [...shifts].sort((a, b) => toMillis(a.endTime) - toMillis(b.endTime));
  const weekStart = sortedByEnd[0]?.startTime || null;
  const weekEnd = sortedByEnd[sortedByEnd.length - 1]?.endTime || null;

  if (!tenantId || tenantId === "all" || shifts.some(s => s.tenantId !== tenantId)) throw new Error("Invoice shifts must belong to one authorized tenant.");
  return {
    tenantId,
    facilityId: String(facilityId).toLowerCase(),
    facilityName,
    weekStart,
    weekEnd,
    billingStart: weekStart,
    billingEnd: weekEnd,
    status: "draft",
    shiftIds: shifts.map((s) => s.id),

    lines,
    lineItems: lines, // backward-safe alias

    totalHours,
    subtotal,
    adjustments: roundMoney(adjustments),
    totalAmount,
    total: totalAmount, // backward-safe alias
  };
}

async function getNextInvoiceNumber(tenantId) {
  const year = new Date().getFullYear();
  const invoicesRef = collection(db, "invoices");
  const snap = await getDocs(query(invoicesRef, where("tenantId", "==", tenantId), orderBy("createdAt", "desc")));

  let maxSeq = 0;

  snap.docs.forEach((d) => {
    const data = d.data() || {};
    const num = String(data.invoiceNumber || "");
    const match = num.match(/^INV-(\d{4})-(\d{4,})$/);
    if (!match) return;
    const [, yyyy, seq] = match;
    if (Number(yyyy) !== year) return;
    const seqNum = Number(seq);
    if (Number.isFinite(seqNum) && seqNum > maxSeq) maxSeq = seqNum;
  });

  const next = String(maxSeq + 1).padStart(4, "0");
  return `INV-${year}-${next}`;
}

export async function createInvoiceFromDraft(draft, adminUid = "") {
  if (!draft || !Array.isArray(draft.shiftIds) || draft.shiftIds.length === 0) {
    throw new Error("Invalid invoice draft.");
  }

  if (!draft.tenantId || draft.tenantId === "all") throw new Error("Invoice tenantId is required.");
  const invoiceNumber = await getNextInvoiceNumber(draft.tenantId);
  const invoiceDate = new Date();
  const dueDate = makeDueDateFrom(invoiceDate);

  const lines = Array.isArray(draft.lines)
    ? draft.lines
    : Array.isArray(draft.lineItems)
    ? draft.lineItems
    : [];

  const totalHours =
    roundHours(draft.totalHours) ||
    roundHours(lines.reduce((sum, item) => sum + Number(item.hours || 0), 0));

  const subtotal =
    roundMoney(draft.subtotal) ||
    roundMoney(lines.reduce((sum, item) => sum + Number(item.lineTotal || item.amount || 0), 0));

  const adjustments = roundMoney(draft.adjustments || 0);
  const totalAmount = roundMoney(
    draft.totalAmount ?? draft.total ?? subtotal + adjustments
  );

  const payload = {
    tenantId: draft.tenantId,
    facilityId: String(draft.facilityId || "").toLowerCase(),
    facilityName: draft.facilityName || "",
    invoiceNumber,
    invoiceDate,
    dueDate,

    weekStart: draft.weekStart || draft.billingStart || null,
    weekEnd: draft.weekEnd || draft.billingEnd || null,

    // backward-safe aliases
    billingStart: draft.weekStart || draft.billingStart || null,
    billingEnd: draft.weekEnd || draft.billingEnd || null,

    status: "draft",
    shiftIds: draft.shiftIds,

    lines,
    lineItems: lines, // backward-safe alias

    totalHours,
    subtotal,
    adjustments,
    totalAmount,
    total: totalAmount, // backward-safe alias

    factoringCompany: "Riviera Finance",
    factoringStatus: "not_submitted",

    generatedBy: adminUid || "admin",
    createdBy: adminUid || "admin",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const ref = await addDoc(collection(db, "invoices"), payload);

  for (const shiftId of draft.shiftIds) {
    await updateDoc(doc(db, "shifts", shiftId), {
      invoiceId: ref.id,
      invoiceNumber,
      invoiceStatus: "billed",
      billedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }

  return {
    id: ref.id,
    invoiceNumber,
    ...payload,
  };
}

export async function markInvoiceSent(invoiceId) {
  if (!invoiceId) throw new Error("invoiceId is required.");
  await updateDoc(doc(db, "invoices", invoiceId), {
    status: "sent",
    sentAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function markInvoicePaid(invoiceId) {
  if (!invoiceId) throw new Error("invoiceId is required.");
  await updateDoc(doc(db, "invoices", invoiceId), {
    status: "paid",
    paidAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function voidInvoice(invoiceId) {
  if (!invoiceId) throw new Error("invoiceId is required.");

  await updateDoc(doc(db, "invoices", invoiceId), {
    status: "void",
    voidedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}